// Az alkalmazás váza a weben: oldalnavigáció + fejléc + tartalom.
//
// A négy töréspont a DS06 átadási leírásból jön, és megegyezik a Flutter
// oldalival (lib/base/ui/ds/blaze_breakpoints.dart):
//   < 600 px    alsó navigáció
//   600-1024    68 px-es mini oldalsáv
//   1024-1440   248 px-es oldalsáv + kétpaneles tartalom
//   > 1440      ugyanaz, 1320 px-re fogott tartalommal
//
// A váltást CSS médialekérdezések végzik, nem JavaScript: így nincs
// újrarajzolás átméretezéskor, és a szerver oldali kimenet is helyes lenne.

import { t } from './i18n';
import { h, icon, icons } from './dom';

export type NavItem = {
  id: string;
  label: string;
  /// SVG path a 16x16-os rácson.
  iconPath: string;
  badge?: number;
  children?: { id: string; label: string }[];
};

export type ShellOptions = {
  items: NavItem[];
  activeId: string;
  onNavigate: (id: string) => void;
  user: { name: string; email: string; company?: string };
  /// A saját profil megnyitása. A W25 képernyő eddig SEHONNAN nem nyílt:
  /// megvolt, de egyetlen útvonal sem vezetett hozzá.
  onOpenProfile?: () => void;
  /// Az értesítési központ megnyitása. A W33 képernyő ugyanígy megvolt, de
  /// sehonnan nem nyílt — se csengő, se útvonal nem vezetett hozzá.
  onOpenNotifications?: () => void;
  onSignOut: () => void;
};

export type Shell = {
  root: HTMLElement;
  /// A fő tartalom cseréje (lista, űrlap, bármi).
  setContent: (content: HTMLElement) => void;
  /// A kétpaneles nézet jobb oldala; `null` bezárja a panelt.
  setDetail: (detail: HTMLElement | null) => void;
  setTitle: (title: string) => void;
  setActive: (id: string) => void;
  /// Rovid visszajelzes a kepernyo aljan (ds.css `.toast`).
  /// Az olvasatlan értesítések jelzése a csengőn.
  setUnread: (unread: boolean) => void;
  toast: (message: string, kind?: 'ok' | 'bad' | 'inf') => void;
  /// A kapcsolat és a függő mentések sávja (4.2). `online: false` esetén
  /// mindig látszik; kapcsolattal csak akkor, ha van még fel nem ment
  /// módosítás.
  setConnection: (state: { online: boolean; pending: number }) => void;
};

/// Monogram a névből — ugyanaz a szabály, mint a Flutter BlazeAvatarban.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function createShell(options: ShellOptions): Shell {
  const { items, onNavigate, user, onSignOut, onOpenProfile, onOpenNotifications } = options;
  let activeId = options.activeId;

  const navHost = h('nav', { class: 'col g4', 'aria-label': t('nav.mainLabel') });
  const bottomNavHost = h('nav', {
    class: 'bottomnav',
    'aria-label': t('nav.mainLabel'),
  });
  const titleEl = h('h1', { class: 'h3 grow', text: '' });
  const contentHost = h('div', { class: 'shell-content' });
  const detailHost = h('div', { class: 'shell-detail', style: { display: 'none' } });

  function navButton(
    id: string,
    label: string,
    iconPath: string | null,
    badge: number | undefined,
    sub: boolean,
  ): HTMLElement {
    const active = id === activeId;
    return h(
      'button',
      {
        type: 'button',
        class: `navitem${sub ? ' navsub' : ''}${active ? ' on' : ''}`,
        'aria-current': active ? 'page' : undefined,
        onClick: () => onNavigate(id),
      },
      iconPath ? h('span', { class: 'ic' }, icon(iconPath)) : null,
      h('span', { class: 'grow nav-label', text: label }),
      badge && badge > 0
        ? h('span', {
            class: 'badge badge-sq st-rej',
            text: String(badge),
            'aria-label': `${badge} új`,
          })
        : null,
    );
  }

  function renderNav(): void {
    navHost.replaceChildren(
      ...items.flatMap((item) => {
        const rows: HTMLElement[] = [
          navButton(item.id, item.label, item.iconPath, item.badge, false),
        ];
        // Az almenü csak az aktív ág alatt nyílik ki — különben a 7 menüpont
        // almenüi együtt hosszabbak lennének, mint a képernyő.
        const isBranchActive =
          item.id === activeId || item.children?.some((c) => c.id === activeId);
        if (item.children && isBranchActive) {
          rows.push(
            ...item.children.map((child) =>
              navButton(child.id, child.label, null, undefined, true),
            ),
          );
        }
        return rows;
      }),
    );

    bottomNavHost.replaceChildren(
      // Mobilon nincs hely hétre: az első öt fő pont fér el.
      ...items.slice(0, 5).map((item) => {
        const active =
          item.id === activeId || (item.children?.some((c) => c.id === activeId) ?? false);
        return h(
          'button',
          {
            type: 'button',
            class: `bnitem${active ? ' on' : ''}`,
            'aria-current': active ? 'page' : undefined,
            onClick: () => onNavigate(item.id),
          },
          h('span', { class: 'ic' }, icon(item.iconPath, 20)),
          h('span', { text: item.label }),
        );
      }),
    );
  }

  renderNav();

  // Az oldalsav osszecsukasa.
  //
  // A dizajnrendszerben MAR letezett a `.sidebar.mini` (68 px), csak kapcsolo
  // nem tartozott hozza: az osszecsukas eddig kizarolag a toresponttol
  // fuggott, a felhasznalo nem donthetett rola.
  //
  // A valasztas a bongeszoben marad meg. A localStorage privat ablakban vagy
  // letiltott tarolasnal DOBHAT, ezert minden hozzaferes vedve van — egy
  // elszallo beallitas nem akadalyozhatja meg az app indulasat.
  const MINI_KEY = 'blazedata.sidebar.mini';

  function readMini(): boolean {
    try {
      return window.localStorage.getItem(MINI_KEY) === '1';
    } catch {
      return false;
    }
  }

  function writeMini(value: boolean): void {
    try {
      window.localStorage.setItem(MINI_KEY, value ? '1' : '0');
    } catch {
      // Nem baj: a valasztas csak erre a munkamenetre marad meg.
    }
  }

  let mini = readMini();

  const collapseButton = h('button', {
    class: 'btn btn-ghost btn-sm sidebar-toggle',
    type: 'button',
    onClick: () => {
      mini = !mini;
      writeMini(mini);
      applyMini();
    },
  });

  function applyMini(): void {
    sidebar.classList.toggle('mini', mini);
    const label = mini ? t('nav.expandSidebar') : t('nav.collapseSidebar');
    collapseButton.setAttribute('aria-label', label);
    collapseButton.setAttribute('title', label);
    collapseButton.replaceChildren(
      h('span', { class: 'ic' }, icon(mini ? CHEVRON_RIGHT : CHEVRON_LEFT)),
    );
  }

  const sidebar = h(
    'aside',
    { class: 'sidebar' },
    h(
      'div',
      { class: 'sidebar-head row between g8' },
      // A jel a `.mk` narancs csempeje BELSEJEBE valo. Enelkul a csempe ures
      // maradt, es a fejlecben csak egy nevtelen narancs negyzet allt — a
      // tervben (W05, W38) csillag van benne.
      h(
        'span',
        { class: 'logo' },
        h(
          'span',
          { class: 'mk' },
          h('span', { class: 'ic', style: { color: '#fff' } }, icon(navIcons.ai, 15)),
        ),
        h('span', { class: 'nav-label', text: 'Blaze' }),
      ),
      collapseButton,
    ),
    h('div', { class: 'sidebar-nav' }, navHost),
    h(
      'div',
      { class: 'sidebar-foot' },
      h(
        onOpenProfile ? 'button' : 'div',
        {
          class: 'row g12',
          type: onOpenProfile ? 'button' : undefined,
          'aria-label': onOpenProfile ? t('nav.profile') : undefined,
          style: onOpenProfile
            ? {
                width: '100%',
                border: '0',
                background: 'none',
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
                textAlign: 'left',
                padding: '0',
              }
            : undefined,
          onClick: onOpenProfile,
        },
        h('span', { class: 'av av-b', text: initials(user.name) }),
        h(
          'span',
          { class: 'col nav-label', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
          h('span', { class: 'body-sm w6', text: user.name }),
          h('span', { class: 'caption', text: user.company ?? user.email }),
        ),
      ),
    ),
  );

  /// A terv fejlecében csengő áll, olvasatlan jelzéssel (`.notif-dot`).
  const bell = h(
    'button',
    {
      class: 'btn btn-ghost btn-icon',
      type: 'button',
      'aria-label': t('noti.title'),
      style: { position: 'relative', display: onOpenNotifications ? undefined : 'none' },
      onClick: onOpenNotifications,
    },
    h('span', { class: 'ic' }, icon(icons.bell, 19)),
  );

  const bellDot = h('span', { class: 'notif-dot', style: { display: 'none' } });
  bell.append(bellDot);

  const topbar = h(
    'header',
    { class: 'topbar' },
    titleEl,
    h('span', { class: 'body-sm ts hide-sm', text: user.email }),
    bell,
    h('button', {
      class: 'btn btn-secondary btn-sm',
      type: 'button',
      text: t('user.logout'),
      onClick: onSignOut,
    }),
  );

  const toastHost = h('div', {
    class: 'toast-host',
    role: 'status',
    'aria-live': 'polite',
  });

  // A kapcsolat sávja a fejléc ALATT, a tartalom FÖLÖTT áll: ott van, ahol a
  // felhasználó a tartalmat keresi, és nem takar el semmit.
  const connectionBar = h('div', {
    class: 'row g8',
    role: 'status',
    'aria-live': 'polite',
    style: {
      display: 'none',
      alignItems: 'center',
      padding: '8px 20px',
      borderBottom: '1px solid var(--border)',
      background: 'var(--warn-soft)',
      color: 'var(--warn)',
    },
  });

  applyMini();

  const root = h(
    'div',
    { class: 'shell' },
    sidebar,
    h(
      'div',
      { class: 'shell-main' },
      topbar,
      connectionBar,
      h('div', { class: 'shell-body' }, contentHost, detailHost),
      bottomNavHost,
    ),
    toastHost,
  );

  return {
    root,
    setContent(content) {
      contentHost.replaceChildren(content);
    },
    setDetail(detail) {
      if (detail === null) {
        detailHost.replaceChildren();
        detailHost.style.display = 'none';
        root.classList.remove('has-detail');
      } else {
        detailHost.replaceChildren(detail);
        detailHost.style.display = '';
        root.classList.add('has-detail');
      }
    },
    setTitle(title) {
      titleEl.textContent = title;
      document.title = title ? `${title} · BlazeData` : 'BlazeData';
    },
    setActive(id) {
      activeId = id;
      renderNav();
    },
    setUnread(unread) {
      bellDot.style.display = unread ? '' : 'none';
    },
    setConnection({ online, pending }) {
      if (online && pending === 0) {
        connectionBar.style.display = 'none';
        connectionBar.replaceChildren();
        return;
      }
      connectionBar.style.display = 'flex';
      connectionBar.replaceChildren(
        h(
          'span',
          { class: 'ic', style: { width: '15px', height: '15px', flex: 'none' } },
          icon(icons.wifiOff, 15),
        ),
        h('span', {
          class: 'body-sm w6',
          text: online ? t('app.pendingCount', [String(pending)]) : t('app.offlineTitle'),
        }),
        h('span', {
          class: 'caption hide-sm',
          style: { color: 'inherit', opacity: '.9' },
          text: online ? '' : t('app.offlineBody'),
        }),
      );
    },
    toast(message, kind = 'inf') {
      const item = h('div', { class: `toast ${kind}` }, h('span', { class: 'body-sm', text: message }));
      toastHost.append(item);
      // Az uzenet magatol eltunik; a szoveg addig a role="status" miatt
      // a kepernyoolvasohoz is eljut.
      window.setTimeout(() => item.remove(), 4000);
    },
  };
}

/// A tervek navigációja (W05 oldalsáv).
export const navIcons = {
  tasks: 'M5.5 8.2l2 2 3.5-4M2.8 3.2h10.4v9.6H2.8z',
  projects: 'M2.5 4.2h4l1.2 1.6h5.8v6.8H2.5z',
  calendar: 'M2.8 4.4h10.4v8.4H2.8zM5.4 2.4v3M10.6 2.4v3M2.8 7h10.4',
  people: 'M5.8 7.6a2.1 2.1 0 100-4.2 2.1 2.1 0 000 4.2zM2.4 13.2c0-2 1.5-3.4 3.4-3.4s3.4 1.4 3.4 3.4M10.6 5.2a1.8 1.8 0 010 3.4M11.6 9.9c1.4.3 2.4 1.5 2.4 3',
  ai: 'M8 1.8l1.5 4.2 4.7 1.5-4.7 1.5L8 14.2 6.5 9 1.8 7.5 6.5 6z',
  reports: 'M3 13V7.4M6.6 13V3.2M10.2 13V9M13.4 13V5.6',
  settings: 'M8 10.1a2.1 2.1 0 100-4.2 2.1 2.1 0 000 4.2zM8 1.9l1 1.7 1.9-.4.5 1.9 1.8.8-1 1.7 1 1.7-1.8.8-.5 1.9-1.9-.4-1 1.7-1-1.7-1.9.4-.5-1.9-1.8-.8 1-1.7-1-1.7 1.8-.8.5-1.9 1.9.4z',
} as const;

/// A menü alapértelmezett elemei.
///
/// FÜGGVÉNY, nem konstans: modul szintű konstansként a fájl betöltésekor
/// állt volna össze, a szótár viszont csak a render előtt érkezik meg —
/// vagyis a menü a nyers kulcsokkal fagyott volna be.
/// Az osszecsukas iranyjelzoi. Nem a `navIcons`-ba valok: azok menupontok
/// jelei, ez pedig vezerlo.
const CHEVRON_LEFT = 'M10 4L6 8l4 4';
const CHEVRON_RIGHT = 'M6 4l4 4-4 4';

export function defaultNavItems(options?: {
  /// Van-e AI-s helye a felhasználónak. Hamisnál az „AI felvétel" menüpont
  /// EL SEM KÉSZÜL — nem letiltva jelenik meg, mert egy szürke menüpont csak
  /// bosszantana olyasmivel, amit a felhasználó nem tud megoldani; a
  /// beszerzés a cégadmin dolga.
  ///
  /// FONTOS: ez CSAK a felület. A menü elrejtése önmagában nem korlát — az
  /// AI-végpontnak szerveroldalon is ellenőriznie kell a `seatTier`-t,
  /// különben a funkció közvetlen hívással elérhető marad.
  aiSeat?: boolean;
}): NavItem[] {
  return [
    {
      id: 'tasks',
      label: t('nav.tasks'),
      iconPath: navIcons.tasks,
      children: [
        { id: 'tasks/open', label: t('nav.tasksOpen') },
        { id: 'tasks/mine', label: t('nav.tasksMine') },
        { id: 'tasks/previous', label: t('nav.tasksPrevious') },
        { id: 'tasks/all', label: t('nav.tasksAll') },
      ],
    },
    { id: 'projects', label: t('nav.projects'), iconPath: navIcons.projects },
    { id: 'calendar', label: t('nav.calendar'), iconPath: navIcons.calendar },
    { id: 'people', label: t('nav.people'), iconPath: navIcons.people },
    ...(options?.aiSeat === false
      ? []
      : [{ id: 'ai', label: t('nav.ai'), iconPath: navIcons.ai }]),
    { id: 'reports', label: t('nav.reports'), iconPath: navIcons.reports },
    { id: 'settings', label: t('nav.settings'), iconPath: navIcons.settings },
  ];
}
